import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Translate } from 'react-localize-redux';
import styled, { css } from 'styled-components';

import * as images from './character-images';
import CharacterModal from './character-modal';
import { slideIn } from './keyframes';
import Section from './section';

const characters = ['kogami', 'tsunemori', 'kamino', 'karanomori'];

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0 -1em;

  @media (min-width: 768px) {
    margin: 0 auto;
    width: 25em;
  }
`;

const Item = styled.div`
  cursor: pointer;
  flex: 1 0 34%;
  margin: 1.5em 0.5em;
  position: relative;

  &:last-child {
    max-width: calc(50% - 1em);
  }

  opacity: 0;
  transform: translate(-100%, -2.5em);

  ${({ inView, delay }) =>
    inView &&
    css`
      animation: ${slideIn} 0.7s cubic-bezier(0, 1, 0, 1) ${delay} both;
    `}
`;

const NameContainer = styled.div`
  bottom: 0;
  margin-bottom: -1.5em;
  position: absolute;
  right: 0;
  text-align: center;
`;

const Id = styled.div`
  background: #ffffff;
  font-size: 0.7em;
  padding: 0.3em 1.25em;
`;

const Name = styled.div`
  background: #111111;
  color: #ffffff;
  font-family: 'Noto Serif JP', serif;
  font-size: 1.25em;
  padding: 0.5em 1em;
`;

const Image = styled.img`
  display: block;
  transition: transform 0.2s ease-out;
  width: 100%;

  ${Item}:hover & {
    transform: scale(1.05);
  }
`;

const Character = ({ image, character, ...rest }) => (
  <Item {...rest}>
    <Image alt={character} src={image} />
    <NameContainer>
      <Id>
        <Translate id={`psyLandingPage.characters.${character}.id`} />
      </Id>
      <Name>
        <Translate id={`psyLandingPage.characters.${character}.name`} />
      </Name>
    </NameContainer>
  </Item>
);

const Characters = () => {
  const [openCharacter, setOpenCharacter] = useState('');
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 1 });

  const onNext = event => {
    event.stopPropagation();
    const index = characters.indexOf(openCharacter);
    if (index !== -1) {
      setOpenCharacter(characters[(index + 1) % characters.length]);
    }
  };

  const onPrevious = event => {
    event.stopPropagation();
    const index = characters.indexOf(openCharacter);
    if (index !== -1) {
      setOpenCharacter(
        characters[(index - 1 + characters.length) % characters.length]
      );
    }
  };

  return (
    <Section
      heading="CHARACTERS"
      headingPosition="right"
      slideIn="right"
      subHeading={<Translate id="psyLandingPage.characters.subtitle" />}
    >
      <div ref={ref} />
      <Wrapper>
        {characters.map((char, index) => (
          <Character
            character={char}
            delay={`${index * 0.2}s`}
            // eslint-disable-next-line import/namespace
            image={images[char].top}
            inView={inView}
            key={char}
            onClick={() => setOpenCharacter(char)}
          />
        ))}
        {openCharacter && (
          <CharacterModal
            character={openCharacter}
            // eslint-disable-next-line import/namespace
            image={images[openCharacter].modal}
            onClose={() => setOpenCharacter('')}
            onNext={onNext}
            onPrevious={onPrevious}
            showClose={false}
          />
        )}
      </Wrapper>
    </Section>
  );
};

export default Characters;
